import React, { useState, useEffect } from 'react';
import { Crown, Gift, Check, Sparkles, Infinity } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { simulatePurchase, getPricingInfo, PRICING_CONFIG, recordPremiumPurchase } from '../utils/purchases';
import { recordPurchase as apiRecordPurchase, getPurchaseStatus } from '../utils/api';
import { toast } from 'sonner';
import { Capacitor } from '@capacitor/core';
import { purchasePremium, restorePremium, getPremiumProduct } from '../utils/nativePurchases';

export default function PurchasePaywall({ open, onClose, onPurchaseComplete }) {
  const [purchasing, setPurchasing] = React.useState(false);
  const [summary, setSummary] = useState({
    freeMeetsUsed: 0,
    freeMeetLimit: PRICING_CONFIG.FREE_MEET_LIMIT
  });
  const [livePrice, setLivePrice] = useState(null);
  const pricing = getPricingInfo();
  
  const isNative = Capacitor.isNativePlatform();

  // Load purchase status from API + live store price when dialog opens
  useEffect(() => {
    if (open) {
      (async () => {
        try {
          const status = await getPurchaseStatus();
          setSummary({
            freeMeetsUsed: Math.min(status.meets_created, PRICING_CONFIG.FREE_MEET_LIMIT),
            freeMeetLimit: PRICING_CONFIG.FREE_MEET_LIMIT
          });
        } catch (error) {
          console.error('Failed to load purchase status:', error);
        }
        if (isNative) {
          const product = await getPremiumProduct();
          if (product && (product.priceString || product.price)) {
            setLivePrice(product.priceString || `$${product.price}`);
          }
        }
      })();
    }
  }, [open, isNative]);

  const handlePurchase = async () => {
    setPurchasing(true);
    
    try {
      if (isNative) {
        // Real native IAP flow (StoreKit on iOS, Google Play Billing on Android)
        const { transactionId } = await purchasePremium();
        recordPremiumPurchase(transactionId);
        await apiRecordPurchase(transactionId);
        toast.success('Premium unlocked! Enjoy unlimited meets.');
        onPurchaseComplete?.();
        onClose();
      } else {
        // Web fallback - simulate purchase for testing
        simulatePurchase();
        await apiRecordPurchase(`web-test-${Date.now()}`);
        toast.success('Test purchase successful! You now have unlimited meet creation.');
        onPurchaseComplete?.();
        onClose();
      }
    } catch (error) {
      console.error('Purchase error:', error);
      const msg = error?.message || '';
      if (/cancel/i.test(msg)) {
        toast.info('Purchase canceled.');
      } else {
        toast.error('Purchase failed. Please try again.');
      }
    } finally {
      setPurchasing(false);
    }
  };

  const handleRestorePurchases = async () => {
    setPurchasing(true);
    
    try {
      if (isNative) {
        const { restored, transactionId } = await restorePremium();
        if (restored) {
          recordPremiumPurchase(transactionId);
          await apiRecordPurchase(transactionId);
          toast.success('Premium restored!');
          onPurchaseComplete?.();
          onClose();
        } else {
          toast.info('No previous purchases found on this account.');
        }
      } else {
        toast.info('Restore is only available on the iOS / Android app.');
      }
    } catch (error) {
      console.error('Restore error:', error);
      toast.error('Failed to restore purchases.');
    } finally {
      setPurchasing(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl">
            <Crown className="w-6 h-6 text-amber-500" />
            Upgrade to Premium
          </DialogTitle>
          <DialogDescription>
            You&apos;ve used your {summary.freeMeetsUsed} free meet creations. Unlock unlimited meets with a one-time purchase.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Usage Summary */}
          <Card className="p-4 bg-muted/50">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Gift className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium">Free Trial</span>
              </div>
              <Badge variant="secondary">
                {summary.freeMeetsUsed} / {summary.freeMeetLimit} meets used
              </Badge>
            </div>
          </Card>

          {/* Premium Option */}
          <Card className="p-5 border-2 border-amber-400 bg-gradient-to-br from-amber-50 to-orange-50">
            <div className="flex items-start gap-4">
              <div className="p-2 rounded-full bg-amber-100">
                <Sparkles className="w-6 h-6 text-amber-600" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between">
                  <h3 className="font-bold text-lg">Premium Unlock</h3>
                  <div className="text-right">
                    <p className="text-2xl font-bold text-amber-600">{livePrice || pricing.priceDisplay}</p>
                    <p className="text-xs text-muted-foreground">one-time</p>
                  </div>
                </div>
                
                <ul className="mt-3 space-y-2">
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span>Unlimited meet creation</span>
                    <Infinity className="w-4 h-4 text-amber-600" />
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span>All current features</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span>Future updates included</span>
                  </li>
                  <li className="flex items-center gap-2 text-sm">
                    <Check className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span>No subscription - pay once, own forever</span>
                  </li>
                </ul>
              </div>
            </div>
          </Card>

          {/* Info Text */}
          <p className="text-xs text-muted-foreground text-center">
            Purchases are tied to your Apple ID or Google account. Use &ldquo;Restore Purchases&rdquo; on a new device to recover.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col gap-2">
          <Button 
            size="lg" 
            className="w-full bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-semibold"
            onClick={handlePurchase}
            disabled={purchasing}
          >
            {purchasing ? 'Processing...' : `Unlock Premium for ${livePrice || pricing.priceDisplay}`}
          </Button>
          <Button 
            variant="ghost" 
            size="sm"
            onClick={handleRestorePurchases}
            disabled={purchasing}
          >
            Restore Purchases
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
